import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { Modal, Spinner, ListGroup, Badge } from "react-bootstrap";
import { API_URL } from "../../apiConfig";
import { Bars3Icon, PlusIcon, ArrowDownTrayIcon, MagnifyingGlassIcon, XMarkIcon, ChevronUpDownIcon, CheckCircleIcon, TagIcon, MapPinIcon, UsersIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";

const T = {
  ink:        "#1a1a1c",
  ink2:       "#4a4a50",
  ink3:       "#86868b",
  ink4:       "#b4b3b0",
  hair:       "rgba(20,20,22,0.07)",
  hair2:      "rgba(20,20,22,0.04)",
  surface:    "rgba(255,255,255,0.72)",
  surface2:   "#f9f8f6",
  accent:     "#1e3a8a",
  accentSoft: "rgba(30,58,138,0.09)",
  ok:         "#1E8E3E",
  okSoft:     "rgba(30,142,62,0.10)",
  warn:       "#B25E09",
  warnSoft:   "rgba(178,94,9,0.10)",
  danger:     "#C5221F",
  dangerSoft: "rgba(197,34,31,0.10)",
  shadowSm:   "0 1px 2px rgba(20,20,22,0.04),0 1px 1px rgba(20,20,22,0.03)",
  shadowMd:   "0 6px 24px -8px rgba(20,20,22,0.08),0 2px 6px rgba(20,20,22,0.04)",
  font:       "'Geist',-apple-system,BlinkMacSystemFont,'SF Pro Text',system-ui,sans-serif",
  r:          18,
};

const cardStyle = {
  background: T.surface,
  backdropFilter: "blur(28px) saturate(180%)",
  WebkitBackdropFilter: "blur(28px) saturate(180%)",
  border: `1px solid ${T.hair}`,
  borderRadius: T.r,
  boxShadow: T.shadowMd,
};

const LOGO_COLORS = ["#1e3a8a","#0f5c2e","#7c2d12","#4c1d95","#b45309","#0f766e","#be185d","#1d4ed8"];
function logoColor(name = "") {
  const n = (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return LOGO_COLORS[n % LOGO_COLORS.length];
}
function logoInitials(name = "") {
  return (name || "").split(" ").filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase() || "?";
}

const DEFAULT_LIST_LIMIT = 20;
const DEFAULT_PUBLIC_ORDER = 999999;

const normalizeText = (v = "") =>
  String(v).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const asText = (v) => String(v ?? "").toLowerCase();
const asTime = (v) => (v ? new Date(v).getTime() : 0);

function roleLabel(role) {
  return (role || "").toLowerCase() === "admin" ? "admin" : "parceiro";
}

function makeComparator(sortBy, sortDir) {
  return (a, b) => {
    let av, bv;
    switch (sortBy) {
      case "company":      av = asText(a.company);    bv = asText(b.company);    break;
      case "benefit":      av = asText(a.benefit);    bv = asText(b.benefit);    break;
      case "role":         av = asText(roleLabel(a.role)); bv = asText(roleLabel(b.role)); break;
      case "category":     av = asText(a.category);   bv = asText(b.category);   break;
      case "city":         av = asText(a.city);        bv = asText(b.city);        break;
      case "active":       av = a.active ? 1 : 0;     bv = b.active ? 1 : 0;     break;
      case "createdAt":    av = asTime(a.createdAt);  bv = asTime(b.createdAt);  break;
      case "lastLoginAt":  av = asTime(b.lastLoginAt); bv = asTime(a.lastLoginAt); break;
      case "displayOrder": av = typeof a.displayOrder === "number" ? a.displayOrder : DEFAULT_PUBLIC_ORDER;
                           bv = typeof b.displayOrder === "number" ? b.displayOrder : DEFAULT_PUBLIC_ORDER; break;
      default:             av = asText(a.company);    bv = asText(b.company);
    }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  };
}

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch { return "—"; }
}

export default function PartnersList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [listLimit, setListLimit] = useState(DEFAULT_LIST_LIMIT);
  const [sortBy, setSortBy] = useState("company");
  const [sortDir, setSortDir] = useState("asc");
  const isFirstAutoLoad = useRef(true);
  const initialLoadDone = useRef(false);

  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderModalItems, setOrderModalItems] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const dragItemId = useRef(null);

  function toggleSort(key) {
    if (sortBy === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir("asc"); }
  }

  function openOrderModal() {
    const sorted = [...items].sort((a, b) => {
      const av = typeof a.displayOrder === "number" ? a.displayOrder : DEFAULT_PUBLIC_ORDER;
      const bv = typeof b.displayOrder === "number" ? b.displayOrder : DEFAULT_PUBLIC_ORDER;
      return av - bv;
    });
    setOrderModalItems(sorted);
    setShowOrderModal(true);
  }

  function handleDrop(targetId) {
    const fromId = dragItemId.current;
    if (!fromId || fromId === targetId) return;
    setOrderModalItems(prev => {
      const fromIdx = prev.findIndex(p => p._id === fromId);
      const toIdx = prev.findIndex(p => p._id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
    setDragOverId(null);
  }

  async function saveOrderModal() {
    setSavingOrder(true);
    const token = localStorage.getItem("token");
    try {
      for (let i = 0; i < orderModalItems.length; i++) {
        const p = orderModalItems[i];
        if (p.displayOrder !== i + 1) {
          await fetch(`${API_URL}/admin/partners/${p._id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ displayOrder: i + 1 }),
          });
        }
      }
      const newOrders = Object.fromEntries(orderModalItems.map((p, i) => [p._id, i + 1]));
      setItems(prev => prev.map(p => newOrders[p._id] !== undefined ? { ...p, displayOrder: newOrders[p._id] } : p));
      setShowOrderModal(false);
    } catch (e) {
      window.alert("Erro ao salvar ordem: " + (e?.message || ""));
    } finally {
      setSavingOrder(false);
    }
  }

  const filteredItems = useMemo(() => {
    const terms = q.trim().split(/\s+/).map(normalizeText).filter(Boolean);
    if (!terms.length) return items;
    return items.filter(item =>
      terms.every(term =>
        ["company","email","category","city","benefit","benefitDescription"].some(f =>
          normalizeText(item[f]).includes(term)
        )
      )
    );
  }, [items, q]);

  const sortedItems = useMemo(() => [...filteredItems].sort(makeComparator(sortBy, sortDir)), [filteredItems, sortBy, sortDir]);
  const visibleItems = useMemo(() => sortedItems.slice(0, Math.min(listLimit, sortedItems.length)), [sortedItems, listLimit]);

  const load = useCallback(async () => {
    setErr("");
    const token = localStorage.getItem("token");
    if (!token || !API_URL) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (statusFilter) params.set("active", statusFilter === "active" ? "true" : "false");
      const r = await fetch(`${API_URL}/admin/partners?${params}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (r.status === 401 || r.status === 403) { setErr("Não autorizado."); return; }
      const data = await r.json();
      if (!r.ok) { setErr(data?.message || "Erro ao buscar parceiros"); return; }
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch { setErr("Falha de conexão com a API"); }
    finally { setLoading(false); }
  }, [q, statusFilter]);

  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;
    load();
  }, [load]);

  const typingTimer = useRef(null);
  useEffect(() => {
    if (isFirstAutoLoad.current) { isFirstAutoLoad.current = false; return; }
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => load(), 400);
    return () => { if (typingTimer.current) clearTimeout(typingTimer.current); };
  }, [q, statusFilter, load]);

  // Stats
  const stats = useMemo(() => ({
    total: items.length,
    active: items.filter(p => p.active).length,
    cats: new Set(items.map(p => p.category).filter(Boolean)).size,
    cities: new Set(items.map(p => p.city).filter(Boolean)).size,
  }), [items]);

  const SortIcon = ({ col }) => (
    <ChevronUpDownIcon style={{ width: 12, height: 12, marginLeft: 4, opacity: sortBy === col ? 1 : 0.3, color: sortBy === col ? T.accent : T.ink3 }} />
  );

  return (
    <div style={{ fontFamily: T.font }}>
      <style>{`
        .ptbl th { text-align:left; font-weight:600; font-size:10.5px; color:${T.ink2}; letter-spacing:.06em; text-transform:uppercase; padding:10px 14px; border-bottom:1px solid ${T.hair}; background:transparent; cursor:pointer; white-space:nowrap; }
        .ptbl td { padding:12px 14px; vertical-align:middle; border-bottom:1px solid ${T.hair2}; font-size:13px; color:${T.ink}; }
        .ptbl tr:last-child td { border-bottom:none; }
        .ptbl tbody tr:hover td { background: ${T.hair2}; }
        .partner-row { cursor:pointer; }
      `}</style>

      {/* Page header */}
      <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12 }}>
        <div>
          <h1 style={{ margin:0, fontSize:28, fontWeight:600, letterSpacing:"-0.025em", color:T.ink }}>Parceiros</h1>
          <div style={{ fontSize:13, color:T.ink3, marginTop:3 }}>
            Exibindo <b style={{ color:T.ink }}>{visibleItems.length}</b> de {sortedItems.length} parceiros
          </div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button onClick={openOrderModal} disabled={items.length === 0}
            style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:10, border:`1px solid ${T.hair}`, background:"transparent", fontSize:13, color:T.ink2, cursor:"pointer", fontFamily:T.font }}>
            <Bars3Icon style={{ width:15, height:15 }}/> Reordenar site
          </button>
          <button onClick={() => navigate("/admin/partners/new")}
            style={{ display:"inline-flex", alignItems:"center", gap:6, padding:"8px 16px", borderRadius:10, border:"none", background:T.accent, fontSize:13, fontWeight:500, color:"#fff", cursor:"pointer", fontFamily:T.font }}>
            <PlusIcon style={{ width:15, height:15 }}/> Novo parceiro
          </button>
        </div>
      </div>

      {err && <div style={{ marginBottom:14, padding:"10px 14px", borderRadius:10, background:T.dangerSoft, color:T.danger, fontSize:13 }}>{err}</div>}

      {/* KPI row */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:14, marginBottom:14 }}>
        {[
          { label:"Parceiros ativos",  value:stats.active, sub:`de ${stats.total} total`,   color:T.ok,     soft:T.okSoft,     icon:<CheckCircleIcon style={{ width:14, height:14 }} /> },
          { label:"Categorias",        value:stats.cats,   sub:"distintas",                 color:T.accent, soft:T.accentSoft, icon:<TagIcon         style={{ width:14, height:14 }} /> },
          { label:"Cidades atendidas", value:stats.cities, sub:"cobertura",                 color:T.warn,   soft:T.warnSoft,   icon:<MapPinIcon      style={{ width:14, height:14 }} /> },
          { label:"Total cadastrados", value:stats.total,  sub:"todos os perfis",           color:T.ink2,   soft:T.hair2,      icon:<UsersIcon       style={{ width:14, height:14 }} /> },
        ].map((k, i) => (
          <div key={i} style={{ background:"#fff", border:"0.5px solid "+T.hair, borderRadius:16, boxShadow:T.shadowSm, padding:"22px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:12, color:T.ink2, fontWeight:500, letterSpacing:"0.01em" }}>
              <span>{k.label}</span>
              <div style={{ width:28, height:28, borderRadius:8, background:k.soft, color:k.color, display:"grid", placeItems:"center", flexShrink:0 }}>{k.icon}</div>
            </div>
            <div style={{ fontSize:42, fontWeight:600, letterSpacing:"-0.03em", lineHeight:1, color:k.color, margin:"14px 0 8px", fontVariantNumeric:"tabular-nums" }}>{loading ? "—" : k.value}</div>
            <div style={{ fontSize:12, color:T.ink3 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ ...cardStyle, padding:"14px 16px", marginBottom:14 }}>
        <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
          <div style={{ flex:1, minWidth:220, position:"relative" }}>
            <MagnifyingGlassIcon style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", width:14, height:14, color:T.ink3 }}/>
            <input
              value={q} onChange={e => setQ(e.target.value)}
              placeholder="Buscar empresa, benefício, categoria…"
              style={{ width:"100%", padding:"8px 32px 8px 32px", borderRadius:10, border:`1px solid ${T.hair}`, fontSize:13, fontFamily:T.font, outline:"none", background:T.surface2, color:T.ink, boxSizing:"border-box" }}
            />
            {q && <button onClick={() => setQ("")} style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:T.ink3, display:"flex" }}><XMarkIcon style={{ width:14, height:14 }}/></button>}
          </div>
        </div>
        <div style={{ display:"flex", gap:6, marginTop:12, flexWrap:"wrap" }}>
          {[{ id:"active", label:"Ativos", n:stats.active }, { id:"", label:"Todos", n:stats.total }, { id:"inactive", label:"Inativos", n:stats.total - stats.active }].map(c => (
            <button key={c.id} onClick={() => setStatusFilter(c.id)}
              style={{ padding:"5px 14px", borderRadius:999, fontSize:12.5, fontWeight:500, cursor:"pointer", fontFamily:T.font, border:`1px solid ${statusFilter === c.id ? T.accent : T.hair}`, background:statusFilter === c.id ? T.accentSoft : "transparent", color:statusFilter === c.id ? T.accent : T.ink2 }}>
              {c.label} <span style={{ opacity:0.7, fontVariantNumeric:"tabular-nums" }}>{c.n}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ ...cardStyle, overflow:"hidden" }}>
        <div style={{ padding:"14px 18px", borderBottom:`1px solid ${T.hair}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span style={{ fontSize:13, fontWeight:600, color:T.ink }}>Lista de Parceiros</span>
          <span style={{ fontSize:12, color:T.ink3 }}>{sortedItems.length} resultados</span>
        </div>
        {loading ? (
          <div style={{ padding:40, textAlign:"center", color:T.ink3 }}>
            <Spinner animation="border" size="sm" style={{ color:T.accent }} />
          </div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table className="ptbl" style={{ width:"100%", borderCollapse:"collapse" }}>
              <thead>
                <tr>
                  <th onClick={() => toggleSort("company")} style={{ paddingLeft:18 }}>Empresa <SortIcon col="company"/></th>
                  <th onClick={() => toggleSort("benefit")}>Benefício <SortIcon col="benefit"/></th>
                  <th onClick={() => toggleSort("displayOrder")} style={{ width:80 }}>Ordem <SortIcon col="displayOrder"/></th>
                  <th onClick={() => toggleSort("role")} style={{ width:100 }}>Tipo <SortIcon col="role"/></th>
                  <th onClick={() => toggleSort("category")}>Categoria <SortIcon col="category"/></th>
                  <th onClick={() => toggleSort("city")}>Cidade <SortIcon col="city"/></th>
                  <th onClick={() => toggleSort("active")}>Status <SortIcon col="active"/></th>
                  <th onClick={() => toggleSort("createdAt")}>Criado em <SortIcon col="createdAt"/></th>
                  <th onClick={() => toggleSort("lastLoginAt")}>Último acesso <SortIcon col="lastLoginAt"/></th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign:"center", padding:32, color:T.ink3 }}>Nenhum parceiro encontrado.</td></tr>
                ) : visibleItems.map(p => (
                  <tr key={p._id || p.email} className="partner-row" onClick={() => navigate(`/admin/partners/${encodeURIComponent(p._id || p.email)}`)}>
                    <td style={{ paddingLeft:18 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:32, height:32, borderRadius:9, background: p.color || logoColor(p.company), color:"#fff", fontSize:11, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                          {logoInitials(p.company)}
                        </div>
                        <div>
                          <div style={{ fontWeight:500 }}>{p.company || "—"}</div>
                          <div style={{ fontSize:11.5, color:T.ink3 }}>{p.email || ""}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ color:T.ink2, maxWidth:260, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.benefit || "—"}</td>
                    <td>
                      {typeof p.displayOrder === "number"
                        ? <span style={{ padding:"2px 8px", borderRadius:6, background:T.hair2, fontSize:12, fontWeight:600, color:T.ink2 }}>#{p.displayOrder}</span>
                        : <span style={{ color:T.ink4 }}>—</span>}
                    </td>
                    <td>
                      {roleLabel(p.role) === "admin"
                        ? <span style={{ padding:"3px 10px", borderRadius:999, fontSize:11.5, fontWeight:500, background:T.okSoft, color:T.ok }}>admin</span>
                        : <span style={{ padding:"3px 10px", borderRadius:999, fontSize:11.5, fontWeight:500, background:T.accentSoft, color:T.accent }}>parceiro</span>}
                    </td>
                    <td style={{ color:T.ink2 }}>{p.category || "—"}</td>
                    <td style={{ color:T.ink2 }}>{p.city || "—"}</td>
                    <td>
                      {p.active
                        ? <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:T.okSoft, color:T.ok }}><span style={{ width:5, height:5, borderRadius:"50%", background:T.ok, display:"inline-block" }}/>Ativo</span>
                        : <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", fontSize:11.5, fontWeight:500, borderRadius:999, background:T.hair2, color:T.ink3 }}><span style={{ width:5, height:5, borderRadius:"50%", background:T.ink3, display:"inline-block" }}/>Inativo</span>}
                    </td>
                    <td style={{ color:T.ink2, fontVariantNumeric:"tabular-nums" }}>{fmtDate(p.createdAt)}</td>
                    <td style={{ color:p.lastLoginAt ? T.ink2 : T.ink4, fontVariantNumeric:"tabular-nums" }}>{fmtDate(p.lastLoginAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {sortedItems.length > DEFAULT_LIST_LIMIT && (
          <div style={{ padding:"14px 18px", borderTop:`1px solid ${T.hair2}`, display:"flex", justifyContent:"center" }}>
            {listLimit <= DEFAULT_LIST_LIMIT
              ? <button onClick={() => setListLimit(500)} style={{ padding:"7px 20px", borderRadius:10, border:`1px solid ${T.hair}`, background:"transparent", fontSize:13, fontWeight:500, cursor:"pointer", color:T.ink2, fontFamily:T.font }}>Mostrar mais</button>
              : <button onClick={() => setListLimit(DEFAULT_LIST_LIMIT)} style={{ padding:"7px 20px", borderRadius:10, border:`1px solid ${T.hair}`, background:"transparent", fontSize:13, fontWeight:500, cursor:"pointer", color:T.ink2, fontFamily:T.font }}>Mostrar menos</button>}
          </div>
        )}
      </div>

      {/* Order modal */}
      <Modal show={showOrderModal} onHide={() => !savingOrder && setShowOrderModal(false)} size="lg" scrollable centered>
        <Modal.Header closeButton={!savingOrder} style={{ borderBottom:`1px solid ${T.hair}`, padding:"16px 24px" }}>
          <Modal.Title style={{ fontSize:16, fontWeight:600, color:T.ink }}>Reordenar Parceiros no Site</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding:"8px 16px" }}>
          <p style={{ fontSize:12.5, color:T.ink3, marginBottom:8 }}>Arraste as linhas para definir a ordem de exibição. A posição #1 aparece primeiro.</p>
          <ListGroup>
            {orderModalItems.map((p, idx) => (
              <ListGroup.Item key={p._id} draggable
                onDragStart={() => { dragItemId.current = p._id; setDraggingId(p._id); }}
                onDragEnd={() => { dragItemId.current = null; setDraggingId(null); setDragOverId(null); }}
                onDragOver={e => { e.preventDefault(); setDragOverId(p._id); }}
                onDragLeave={() => setDragOverId(null)}
                onDrop={e => { e.preventDefault(); handleDrop(p._id); }}
                className="d-flex align-items-center gap-2"
                style={{ cursor:"grab", opacity:draggingId === p._id ? 0.35 : 1, borderLeft:dragOverId === p._id ? `3px solid ${T.accent}` : "3px solid transparent", transition:"border-left .1s, opacity .15s", userSelect:"none" }}>
                <span style={{ color:T.ink4, fontSize:18 }}>⠿</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:500, fontSize:"0.9rem", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{p.company || "—"}</div>
                  <div style={{ fontSize:"0.8rem", color:T.ink3 }}>{p.category || "—"}</div>
                </div>
                {!p.active && <Badge bg="secondary" style={{ fontSize:"0.7rem" }}>Inativo</Badge>}
                <span style={{ fontSize:"0.78rem", color:T.ink3 }}>#{idx + 1}</span>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Modal.Body>
        <Modal.Footer style={{ borderTop:`1px solid ${T.hair}`, padding:"14px 24px", gap:8 }}>
          <button onClick={() => setShowOrderModal(false)} disabled={savingOrder}
            style={{ padding:"8px 16px", borderRadius:10, border:`1px solid ${T.hair}`, background:"transparent", fontSize:13, color:T.ink2, cursor:"pointer", fontFamily:T.font }}>
            Cancelar
          </button>
          <button onClick={saveOrderModal} disabled={savingOrder}
            style={{ padding:"8px 16px", borderRadius:10, border:"none", background:T.accent, fontSize:13, fontWeight:500, color:"#fff", cursor:"pointer", fontFamily:T.font, display:"inline-flex", alignItems:"center", gap:6 }}>
            {savingOrder ? <><Spinner size="sm" animation="border" style={{ width:13, height:13 }}/> Salvando…</> : "Salvar ordem"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
